import { createContext, useContext } from 'react';
import { useFirebase } from './FirebaseContext';

type APIRequest = Pick<RequestInit, 'method' | 'body' | 'signal'>;
type CommonOptions = Pick<RequestInit, 'signal'>;
type LiveSimultaneity = { available: boolean; maxSimultaneousLives: number; simultaneousLives: number };
type BBBServer = {
  active: boolean;
  awsInstanceId: string;
  awsInstanceName: string;
  hasActiveMeeting: boolean;
  id: number;
  liveTitle?: string;
  publicDns: string;
  status: string;
};
type BBBMeetingInfo = {
  meeting: {
    attendeePassword?: string;
    attendees: Array<{
      fullName?: string;
      joinedVoice: boolean;
      listeningOnly: boolean;
      moderator: boolean;
      presenter: boolean;
      userId?: string;
      video: boolean;
    }>;
    createdAt?: string;
    hasUserJoined: boolean;
    internalMeetingId?: string;
    listenerCount: number;
    meetingId?: string;
    meetingName?: string;
    metadata: Record<string, string>;
    moderatorCount: number;
    moderatorPassword?: string;
    participantCount: number;
    startedAt?: string;
    videoCount: number;
    voiceParticipantCount: number;
  } | null;
  server: Pick<BBBServer, 'awsInstanceName' | 'id'>;
};
type LiveAttendance = {
  attendees: Array<{
    minutes: number;
    user: { email: string; firstName?: string | null; id: number; lastName?: string | null };
  }>;
  meetingCount: number;
};

const MY_LIVES_CHECK_INTERVAL_MS = 10 * 60 * 1000;

const APIContext = createContext<{
  getLive: (id: number) => Promise<any>;
  getLiveAttendance: (liveId: number) => Promise<LiveAttendance>;
  getLives: (month: number, year: number, options?: CommonOptions) => Promise<any[]>;
  getBBBServers: () => Promise<BBBServer[]>;
  getBBBServerMeetingInfo: (serverId: number) => Promise<BBBMeetingInfo>;
  checkLiveSimultaneity: (live: { id?: number; startDate: Date; endDate: Date }, options?: CommonOptions) => Promise<LiveSimultaneity>;
  checkMyLives: () => Promise<{ check: boolean }>;
  getMyLives: () => Promise<any[]>;
  getMyPastLives: () => Promise<any[]>;
  getPastLiveFileUrl: (liveId: number, fileId: number) => void;
  createLive: (live: any) => Promise<any>;
  updateLive: (live: any) => Promise<any>;
  deleteLive: (id: number) => Promise<any>;
  getLiveGuestLinks: (liveId: number) => Promise<any[]>;
  createLiveGuestLink: (liveId: number, name: string, role: string) => Promise<any>;
  revokeLiveGuestLink: (liveId: number, linkId: number) => Promise<void>;
  joinLive: (liveId: number) => Promise<void>;
  accessRecording: (liveId: number, recordingId: number) => Promise<void>;
}>(null);

export const useAPI = () => {
  return useContext(APIContext);
};

export const APIProvider = ({ children }) => {
  const { currentUser } = useFirebase();

  const BASEURL = process.env.NODE_ENV == 'development' ? 'http://localhost:3333' : process.env.REACT_APP_API_URL;

  const _call = async (path: string, options: APIRequest): Promise<unknown> => {
    const headers = new Headers();
    if (currentUser) {
      const token = await currentUser.getIdToken();
      headers.set('Authorization', `Bearer ${token}`);
    }
    if (options.body) {
      let isJson = true;
      try {
        JSON.parse(options.body as any);
      } catch {
        isJson = false;
      }
      if (isJson) {
        headers.set('Content-Type', 'application/json; charset=UTF-8');
      }
    }
    const response = await fetch(`${BASEURL}${path}`, { ...options, credentials: 'include', headers });
    if (response.ok) {
      const contentType = response.headers.get('Content-Type');
      if (contentType) {
        if (contentType.match('application/json')) {
          return response.json();
        } else {
          return response.text();
        }
      } else return null;
    }
    console.error(`${options.method} ${path}: ${response.status} ${response.statusText}`);
    throw new Error(response.status.toString());
  };

  const checkMyLives = async () => {
    const cacheKey = `lives_${currentUser.uid}`;
    const cache = localStorage.getItem(cacheKey);
    if (cache == '1') return { check: true };
    const checkedAt = Number(localStorage.getItem(`${cacheKey}_checked_at`));
    if (cache == '0' && Date.now() - checkedAt < MY_LIVES_CHECK_INTERVAL_MS) return { check: false };

    const res = await (_call(`/lives/check`, { method: 'GET' }) as Promise<{ check: boolean }>);
    localStorage.setItem(cacheKey, `${+res.check}`);
    localStorage.setItem(`${cacheKey}_checked_at`, `${Date.now()}`);
    return res;
  };

  const getMyLives = () => _call(`/lives/list`, { method: 'GET' }) as Promise<any[]>;
  const getMyPastLives = () => _call(`/lives/past`, { method: 'GET' }) as Promise<any[]>;
  const getPastLiveFileUrl = async (liveId: number, fileId: number) => {
    const url = new URL(`/lives/past/${liveId}/files/${fileId}`, BASEURL);
    url.searchParams.set('token', await currentUser.getIdToken(true));
    window.location.href = url.toString();
  };

  const getLive = (id: number) => {
    return _call(`/lives/${id}`, { method: 'GET' }) as Promise<any>;
  };

  const getLiveAttendance = (liveId: number) => {
    return _call(`/lives/${liveId}/attendance`, { method: 'GET' }) as Promise<LiveAttendance>;
  };

  const getLives = (month: number, year: number, options?: CommonOptions) => {
    return _call(`/lives/${year}/${month}`, { method: 'GET', ...options }) as Promise<any[]>;
  };

  const getBBBServers = () => _call(`/webinar/servers`, { method: 'GET' }) as Promise<BBBServer[]>;
  const getBBBServerMeetingInfo = (serverId: number) => {
    return _call(`/webinar/servers/${serverId}/meeting-info`, { method: 'GET' }) as Promise<BBBMeetingInfo>;
  };

  const checkLiveSimultaneity = (live: { id?: number; startDate: Date; endDate: Date }, options?: CommonOptions) => {
    const { id, startDate, endDate } = live;
    return _call(`/lives/check-simultaneous`, {
      method: 'POST',
      body: JSON.stringify({ id, startDate: startDate.toISOString(), endDate: endDate.toISOString() }),
      ...options,
    }) as Promise<LiveSimultaneity>;
  };

  const _getLiveFormData = (live: any) => {
    const body = new FormData();
    const { image, files, ...clean } = live;
    if (image) {
      if (image instanceof File) body.set('image', image);
      else clean.persistedImage = 1;
    }
    clean.persistedFiles = [];
    for (const file of files) {
      if (file instanceof File) body.append('files', file);
      else clean.persistedFiles.push(file.id);
    }
    body.set('data', JSON.stringify(clean));
    return body;
  };

  const createLive = (live: any) => {
    const body = _getLiveFormData(live);
    return _call(`/lives`, { method: 'POST', body });
  };

  const updateLive = (live: any) => {
    const body = _getLiveFormData(live);
    return _call(`/lives/${live.id}`, { method: 'PUT', body });
  };

  const deleteLive = (id: number) => {
    return _call(`/lives/${id}`, { method: 'DELETE' });
  };

  const getLiveGuestLinks = (liveId: number) => {
    return _call(`/lives/${liveId}/links`, { method: 'GET' }) as Promise<any[]>;
  };

  const createLiveGuestLink = (liveId: number, name: string, role: string) => {
    return _call(`/lives/${liveId}/links`, { method: 'POST', body: JSON.stringify({ name, role }) }) as Promise<any>;
  };

  const revokeLiveGuestLink = (liveId: number, linkId: number) => {
    return _call(`/lives/${liveId}/links/${linkId}`, { method: 'PATCH', body: JSON.stringify({ active: false }) }) as Promise<void>;
  };

  const trackedTabs = new Map<string, Window>();

  const openAsyncNewTabAndTrack = async (key: string, urlCallback: () => Promise<string>) => {
    const existing = trackedTabs.get(key);

    if (existing) {
      if (!existing.closed) {
        existing.focus();
        return;
      }
      trackedTabs.delete(key);
    }

    const tab = window.open('', '_blank');
    if (!tab) return;

    trackedTabs.set(key, tab);

    tab.document.write(`<html><body>Loading, please wait...</body></html>`);

    try {
      const url = await urlCallback();
      tab.location.replace(url);
      tab.focus();
    } catch (error) {
      tab.close();
      trackedTabs.delete(key);
      throw error;
    }
  };

  const joinLive = (liveId: number) => {
    return openAsyncNewTabAndTrack(`live-${liveId}`, async () => {
      const url = new URL(`/webinar/join/live/${liveId}`, BASEURL);
      url.searchParams.set('token', await currentUser.getIdToken(true));
      return url.toString();
    });
  };

  const accessRecording = (liveId: number, recordingId: number) => {
    return openAsyncNewTabAndTrack(`live-${liveId}-recording-${recordingId}`, async () => {
      const url = new URL(`/lives/${liveId}/recordings/${recordingId}/access`, BASEURL);
      url.searchParams.set('token', await currentUser.getIdToken(true));
      return url.toString();
    });
  };

  return (
    <APIContext.Provider
      value={{
        getLive,
        getLiveAttendance,
        getLives,
        getBBBServers,
        getBBBServerMeetingInfo,
        checkLiveSimultaneity,
        checkMyLives,
        getMyLives,
        getMyPastLives,
        getPastLiveFileUrl,
        createLive,
        updateLive,
        deleteLive,
        getLiveGuestLinks,
        createLiveGuestLink,
        revokeLiveGuestLink,
        joinLive,
        accessRecording,
      }}
    >
      {children}
    </APIContext.Provider>
  );
};
